import { z } from 'zod'

import { recordPath, recordRoute } from '@/shared/registry'

import { classSlots, questionTypes } from './survey'

/** What seeing results, written answers and the export without names needs. */
export const readSurveyResponsesPermission = 'launch.survey-responses.read'

/** What seeing who answered, one response and the export with names also needs. */
export const readRespondentsPermission = 'launch.survey-responses.respondents.read'

/** The two kinds of results page: a class's student survey, or one survey's. */
export type ResultsOf = { readonly kind: 'class'; readonly id: string } | { readonly kind: 'survey'; readonly id: string }

export const classSurveyResultsRoute = '/launch/classes/:publicId/survey-results'
export const surveyResultsRoute = '/launch/surveys/:publicId/results'
export const classTextAnswersRoute = '/launch/classes/:publicId/survey-results/text-answers'
export const surveyTextAnswersRoute = '/launch/surveys/:publicId/results/text-answers'
export const surveyResponseRoute = recordRoute('launch', 'survey-responses')

/** The export Class Details' sidebar asks the results page to start. */
export type ExportKind = 'names' | 'anonymous'

export function resultsPath(of: ResultsOf, exportKind?: ExportKind): string {
  const base = of.kind === 'class' ? `/launch/classes/${encodeURIComponent(of.id)}/survey-results` : `/launch/surveys/${encodeURIComponent(of.id)}/results`
  return exportKind === undefined ? base : `${base}?export=${exportKind}`
}

/** The written answers to a question, with the summary's filters, and the summary's own address to come back to. */
export function textAnswersPath(of: ResultsOf, questionId: string, boxId: string | null, filters: readonly string[], back = ''): string {
  const query = new URLSearchParams({ question: questionId })
  if (back !== '') {
    query.set('back', back)
  }
  if (boxId !== null) {
    query.set('box', boxId)
  }
  for (const filter of filters) {
    query.append('filter', filter)
  }
  return `${resultsPath(of)}/text-answers?${query.toString()}`
}

export function surveyResponsePath(id: string): string {
  return recordPath('launch', 'survey-responses', id)
}

export function resultsApi(of: ResultsOf): string {
  return of.kind === 'class' ? `/launch/v1/classes/${encodeURIComponent(of.id)}/survey-results` : `/launch/v1/surveys/${encodeURIComponent(of.id)}/results`
}

/** The API path with the filters, each sent as one `filter`. */
export function withFilters(path: string, filters: readonly string[], extra: Readonly<Record<string, string>> = {}): string {
  const query = new URLSearchParams(extra)
  for (const filter of filters) {
    query.append('filter', filter)
  }
  const text = query.toString()
  return text === '' ? path : `${path}?${text}`
}

export function exportApi(of: ResultsOf, names: boolean, filters: readonly string[] = []): string {
  return withFilters(`${resultsApi(of)}/export`, filters, names ? { names: 'true' } : {})
}

export function surveyResponseApi(id: string): string {
  return `/launch/v1/survey-responses/${encodeURIComponent(id)}`
}

/** The code of the refusal for a class with no student survey. */
export const noStudentSurveyCode = 'launch.no_student_survey'

/** The code of the refusal for an export of too few responses for the viewer to see their answers. */
export const resultsHiddenCode = 'launch.survey_results_hidden'

const answerSchema = z.object({ id: z.string(), text: z.string() })

const slotSchema = z.enum([...classSlots, 'standalone'])

const responderSchema = z.object({
  id: z.string(),
  slot: slotSchema,
  isNamed: z.boolean(),
  mayOpen: z.boolean(),
  respondentId: z.string().nullish(),
  respondentName: z.string().nullish(),
  // Only for a response the viewer may name.
  submittedOn: z.string().nullish(),
})

/** One response, as a responder list names it. */
export type Responder = z.infer<typeof responderSchema>

export function parseResponder(item: unknown): Responder {
  return responderSchema.parse(item)
}

const resultQuestionSchema = z.object({
  id: z.string(),
  type: z.enum(questionTypes),
  text: z.string(),
  choices: z.array(answerSchema),
  columns: z.array(answerSchema),
  // Too few answered it for a viewer who may not see who did: no numbers come with it.
  answersHidden: z.boolean(),
  answers: z.array(z.object({ answerId: z.string(), rowId: z.string().nullish(), count: z.number().int(), percent: z.number().int() })),
  texts: z.array(z.object({ boxId: z.string().nullish(), count: z.number().int() })),
  averages: z.array(z.object({ rowId: z.string(), average: z.number() })),
  overallAverage: z.number().nullish(),
})

export type ResultQuestion = z.infer<typeof resultQuestionSchema>

const resultsSchema = z.object({
  surveyId: z.string(),
  surveyName: z.string(),
  classId: z.string().nullish(),
  className: z.string().nullish(),
  responseCount: z.number().int(),
  maySeeRespondents: z.boolean(),
  // Too few responses for a viewer who may not see who gave them: no question has numbers.
  answersHidden: z.boolean(),
  fewestResponsesShown: z.number().int(),
  pages: z.array(z.object({ id: z.string(), name: z.string().nullish(), questions: z.array(resultQuestionSchema) })),
  classResponses: z.array(responderSchema),
})

/** A survey's results, as the API gives them. */
export type SurveyResults = z.infer<typeof resultsSchema>

export function parseSurveyResults(body: unknown): SurveyResults {
  return resultsSchema.parse(body)
}

const textAnswersSchema = z.object({
  surveyId: z.string(),
  surveyName: z.string(),
  questionId: z.string(),
  questionText: z.string(),
  boxLabel: z.string().nullish(),
  answersHidden: z.boolean(),
  fewestResponsesShown: z.number().int(),
  // When written shows only to a viewer who may see who wrote it.
  data: z.array(z.object({ id: z.string(), text: z.string().nullish(), isErased: z.boolean(), submittedOn: z.string().nullish() })),
  pagination: z.object({ nextCursor: z.string().nullish(), hasMore: z.boolean() }),
})

export type TextAnswers = z.infer<typeof textAnswersSchema>

export function parseTextAnswers(body: unknown): TextAnswers {
  return textAnswersSchema.parse(body)
}

const givenSchema = z.object({ answerId: z.string().nullish(), rowId: z.string().nullish(), text: z.string().nullish(), isTextErased: z.boolean() })

const responseQuestionSchema = z.object({
  id: z.string(),
  type: z.enum(questionTypes),
  text: z.string(),
  choices: z.array(answerSchema),
  columns: z.array(answerSchema),
  answers: z.array(givenSchema),
})

export type ResponseQuestion = z.infer<typeof responseQuestionSchema>

const responseDetailsSchema = z.object({
  id: z.string(),
  surveyId: z.string(),
  surveyName: z.string(),
  slot: slotSchema,
  classId: z.string().nullish(),
  className: z.string().nullish(),
  respondentId: z.string().nullish(),
  respondentName: z.string().nullish(),
  submittedOn: z.string(),
  pages: z.array(z.object({ id: z.string().nullish(), name: z.string().nullish(), isRemoved: z.boolean(), questions: z.array(responseQuestionSchema) })),
})

/** One response with every question and the answers given. */
export type SurveyResponseDetails = z.infer<typeof responseDetailsSchema>

export function parseSurveyResponseDetails(body: unknown): SurveyResponseDetails {
  return responseDetailsSchema.parse(body)
}

/** The "Survey Taken" choices, as the mockup offers them: the number of days back, or none. */
export const takenChoices = ['anytime', '7', '30', '90'] as const

export type TakenChoice = (typeof takenChoices)[number]

/** When a "Survey Taken" choice starts, from now, or `null` for any time. */
export function takenSince(choice: TakenChoice, now: Date = new Date()): string | null {
  return choice === 'anytime' ? null : new Date(now.getTime() - Number(choice) * 24 * 60 * 60 * 1000).toISOString()
}

/** A course or person a filter names, with the name it shows. */
export interface NamedFilter {
  readonly id: string
  readonly name: string
}

/** A results page's applied filters, as its address keeps them. */
export interface AppliedFilters {
  readonly choice: TakenChoice
  readonly since: string | null
  readonly course: NamedFilter | null
  readonly instructor: NamedFilter | null
}

function named(params: URLSearchParams, key: string): NamedFilter | null {
  const id = params.get(key)
  return id === null || id === '' ? null : { id, name: params.get(`${key}Name`) ?? id }
}

/** The filters the address holds; none when it holds none. */
export function appliedFilters(params: URLSearchParams): AppliedFilters {
  const choice = params.get('taken')
  const since = params.get('since')
  return {
    choice: takenChoices.find((each) => each === choice) ?? 'anytime',
    since: since === null || since === '' ? null : since,
    course: named(params, 'course'),
    instructor: named(params, 'instructor'),
  }
}

/** The address's parameters with these filters, its others kept. */
export function withApplied(params: URLSearchParams, applied: AppliedFilters): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of ['taken', 'since', 'course', 'courseName', 'instructor', 'instructorName']) {
    next.delete(key)
  }
  if (applied.since !== null) {
    next.set('taken', applied.choice)
    next.set('since', applied.since)
  }
  for (const [key, value] of [['course', applied.course], ['instructor', applied.instructor]] as const) {
    if (value !== null) {
      next.set(key, value.id)
      next.set(`${key}Name`, value.name)
    }
  }
  return next
}